import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api';
import type { Booking } from '../../api';
import { useSettings } from '../../context/SettingsContext';
import { parseUtc } from '../../utils/dates';
import { Card, Avatar } from '../ui';
import BookingEditModal from '../BookingEditModal';
import RowTooltip from './RowTooltip';

const DEFAULT_DAYS = 7;

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
}

function describeWhen(startsAt: string, tz: string): { primary: string; secondary: string; isToday: boolean } {
  const start = parseUtc(startsAt);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86_400_000);
  const dayAfter = new Date(tomorrowStart.getTime() + 86_400_000);

  const time = start.toLocaleString('da-DK', { hour: '2-digit', minute: '2-digit', timeZone: tz });

  if (start >= todayStart && start < tomorrowStart) {
    const diffMs = start.getTime() - now.getTime();
    if (diffMs <= 0) return { primary: time, secondary: 'i gang', isToday: true };
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    const inLabel = hours > 0 ? `om ${hours}t ${minutes}m` : `om ${minutes}m`;
    return { primary: time, secondary: `i dag · ${inLabel}`, isToday: true };
  }
  if (start >= tomorrowStart && start < dayAfter) {
    return { primary: time, secondary: 'i morgen', isToday: false };
  }
  const dateStr = start.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  return { primary: time, secondary: dateStr, isToday: false };
}

export default function UpcomingBookingsWidget() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const tz = settings.timezone || 'Europe/Copenhagen';

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<Booking | null>(null);

  const load = () => {
    setLoading(true);
    apiFetch('/api/bookings')
      .then(r => r.json())
      .then((data: Booking[]) => { setBookings(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const now = new Date();
  const cutoff = new Date(Date.now() + DEFAULT_DAYS * 86_400_000);

  // Kommende udlejninger: starts_at i fremtiden
  const relevant = bookings
    .filter(b => !b.cancelled && new Date(b.starts_at) >= now)
    .filter(b => showAll || new Date(b.starts_at) <= cutoff)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  const visible = relevant.slice(0, showAll ? 50 : 5);

  const hasMore = !showAll && bookings.some(b =>
    !b.cancelled && new Date(b.starts_at) > cutoff
  );

  const handleSaved = (updated: Partial<Booking>) => {
    if (!selected) return;
    setBookings(prev => prev.map(b => b.id === selected.id ? { ...b, ...updated } : b));
    setSelected(null);
  };

  return (
    <>
      <Card padded className="h-full">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-base font-semibold text-ink-900 tracking-tight">
            {t('dashboard.widget_upcoming_title', { defaultValue: 'Kommende udlejninger' })}
          </h3>
          {relevant.length > 0 && (
            <span className="text-xs text-ink-500">
              {showAll ? `${relevant.length} i alt` : `næste ${DEFAULT_DAYS} dage`}
            </span>
          )}
        </div>

        {loading && (
          <p className="text-sm text-ink-500 mt-4">…</p>
        )}
        {!loading && relevant.length === 0 && (
          <p className="text-sm text-ink-500 mt-4">
            {t('dashboard.upcoming_none', { defaultValue: 'Ingen kommende udlejninger' })}
          </p>
        )}

        <div className="mt-3 -mx-1">
          {visible.map(item => {
            const when = describeWhen(item.starts_at, tz);
            const fullStart = parseUtc(item.starts_at).toLocaleString('da-DK', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz,
            });
            const fullEnd = parseUtc(item.ends_at).toLocaleString('da-DK', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz,
            });
            return (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className={`relative group/row w-full text-left flex items-center gap-3 px-[15px] py-3 border-b border-cream-100 last:border-b-0 hover:bg-cream-100 rounded-none transition-colors`}
              >
                <Avatar initials={getInitials(item.renter_name)} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink-900 truncate">
                    {item.renter_name || '—'}
                  </div>
                  <div className="text-xs text-ink-500 truncate font-mono">
                    {item.registration_number || '—'}
                    {!item.vehicle_id && (
                      <span className="font-sans text-rose-700 ml-1">· ikke tildelt</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-semibold text-ink-900`}>
                    {when.primary}
                  </div>
                  <div className="text-xs text-ink-500">{when.secondary}</div>
                </div>

                <RowTooltip>
                  <div className="font-semibold mb-1">{item.renter_name || '—'}</div>
                  <div className="font-mono text-ink-500 mb-1.5">
                    {item.registration_number || '—'}
                    {!item.vehicle_id && <span className="font-sans text-rose-700"> · ikke tildelt</span>}
                  </div>
                  <div className="space-y-0.5">
                    <div><span className="text-ink-500">Start:</span> {fullStart}</div>
                    <div><span className="text-ink-500">Aflev:</span> {fullEnd}</div>
                  </div>
                </RowTooltip>
              </button>
            );
          })}
        </div>

        {hasMore && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="mt-3 w-full text-sm text-sage-700 hover:text-sage-900 py-2 text-center font-medium transition-colors"
          >
            {t('dashboard.upcoming_show_more', { defaultValue: 'Vis flere' })} →
          </button>
        )}
      </Card>

      {selected && (
        <BookingEditModal
          booking={selected}
          onClose={() => setSelected(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
